import React, { useEffect, useState } from 'react';
import { Button, Chip, FormControlLabel, Checkbox } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom'; // W3-C
import { FileDownload as FileDownloadIcon, PictureAsPdf as PdfIcon, TableChart as XlsxIcon } from '@mui/icons-material';
import { downloadCsv } from '../../utils/csv';
import { balanceSheetCsv } from '../../utils/financialReportCsv';
import SearchableSelect from '../common/SearchableSelect';
import {
    Account,
    SegmentBalanceSheetResponse,
    SegmentBalanceSheetBlock,
    Segment,
} from '../../interfaces/accounting.interface';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { dayBefore } from '../../utils/reportPeriods';
import {
    ExpandControls,
    ExpandState,
    FigureHeaders,
    SectionLabelRow,
    StatementTreeRows,
    TotalRow,
    collapseAll,
    expandAll,
    initialExpandState,
    ledgerLink,
    statementCellStyle,
    statementHeaderStyle,
    toggleKey,
} from './StatementTree';

const todayStr = toLocalIsoDate();

interface Applied { asOf: string; segmentId?: string; comparePrevYearEnd: boolean }

const SegmentBalanceSheet: React.FC = () => {
    const toast = useToast();
    const [report, setReport] = useState<SegmentBalanceSheetResponse | null>(null);
    const [priorReport, setPriorReport] = useState<SegmentBalanceSheetResponse | null>(null);
    const [applied, setApplied] = useState<Applied | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [expand, setExpand] = useState<ExpandState>(initialExpandState);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [asOf, setAsOf] = useState(todayStr);
    const [comparePrev, setComparePrev] = useState(false);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [selectedSegmentId, setSelectedSegmentId] = useState<string>('');

    useEffect(() => {
        const loadSegments = async () => {
            try {
                const page = await SegmentService.getByInstitution();
                setSegments(page.content ?? []);
            } catch (err) {
                toast.showApiError(err, 'Failed to load segments');
            }
        };
        const loadAccounts = async () => {
            try {
                setAccounts((await AccountService.getAllAccounts(true)) ?? []);
            } catch (err) {
                setAccounts([]);
            }
        };
        loadSegments();
        loadAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const segmentId = selectedSegmentId || undefined;
            const data: SegmentBalanceSheetResponse = await AccountService.getBalanceSheet(asOf, segmentId);
            let prior: SegmentBalanceSheetResponse | null = null;
            if (comparePrev && data?.financialYearStart) {
                prior = await AccountService.getBalanceSheet(dayBefore(data.financialYearStart), segmentId);
            }
            setReport(data);
            setPriorReport(prior);
            setApplied({ asOf, segmentId, comparePrevYearEnd: !!prior });
            setExpand(initialExpandState);
        } catch (err) {
            toast.showApiError(err, 'Failed to load segment balance sheet');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasData = report && report.segments.length > 0;

    // Exports the report on screen (its own dates), never the unapplied inputs.
    const handleExport = () => {
        if (!report) return;
        const { headers, rows } = balanceSheetCsv(report);
        downloadCsv(`balance-sheet-${report.asOfDate}.csv`, headers, rows);
    };

    const handleDownload = async (format: 'pdf' | 'xlsx') => {
        if (!applied) return;
        try {
            setDownloading(true);
            await AccountService.downloadBalanceSheet(applied.asOf, applied.segmentId, applied.comparePrevYearEnd, format);
        } catch (err) {
            toast.showApiError(err, 'Failed to download the balance sheet');
        } finally {
            setDownloading(false);
        }
    };

    const comparatives = priorReport ? [{ label: `As of ${formatDate(priorReport.asOfDate)}`, report: priorReport }] : [];
    const colSpan = 2 + comparatives.length * 3;
    const blockOf = (r: SegmentBalanceSheetResponse | null, segmentId: string) =>
        r?.segments.find(b => b.segmentId === segmentId);
    // Union of segments across both dates, so the blocks add up to the footer.
    const segmentBlocks = (): SegmentBalanceSheetBlock[] => {
        const out = [...(report?.segments ?? [])];
        const seen = new Set(out.map(b => b.segmentId));
        comparatives.forEach(c => c.report.segments.forEach(b => {
            if (!seen.has(b.segmentId)) {
                seen.add(b.segmentId);
                out.push({ segmentId: b.segmentId, segmentCode: b.segmentCode, segmentName: b.segmentName,
                    assetLines: [], liabilityLines: [], equityLines: [], currentPeriodEarnings: 0, retainedEarnings: 0,
                    totalLiabilities: 0, totalEquity: 0, totalAssets: 0, totalLiabilitiesAndEquity: 0, balanced: true });
            }
        }));
        return out;
    };

    const onToggle = (scopedKey: string, expanded: boolean) => setExpand(s => toggleKey(s, scopedKey, expanded));

    // The balance sheet is cumulative, so its ledger drill-down runs from the financial year start.
    const ledgerFrom = (report?.financialYearStart ?? `${(applied?.asOf ?? todayStr).slice(0, 4)}-01-01`);

    const renderHeader = () => (
        <thead>
            <tr>
                <th data-help="accounting.balanceSheet.col.account" style={statementHeaderStyle}>Account</th>
                <FigureHeaders currentLabel={`As of ${report ? formatDate(report.asOfDate) : ''}`} comparativeLabels={comparatives.map(c => c.label)} />
            </tr>
        </thead>
    );

    return (
        <div style={{ padding: '20px' }} data-help-page="accounting.balanceSheet.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ margin: 0 }}>Balance Sheet</h2>
            </div>

            {/* Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <label data-help="accounting.balanceSheet.asOf" style={{ fontWeight: 500 }}>
                    As Of:&nbsp;
                    <input
                        type="date"
                        value={asOf}
                        onChange={e => setAsOf(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                </label>
                <SearchableSelect data-help="accounting.balanceSheet.segment"
                    options={segments}
                    value={selectedSegmentId}
                    onChange={(id) => setSelectedSegmentId(id)}
                    getOptionLabel={(seg) => `${seg.code} — ${seg.name}`}
                    label="Segment"
                    placeholder="All Segments"
                    sx={{ flex: '1 1 200px', maxWidth: 300 }}
                />
                <FormControlLabel data-help="accounting.balanceSheet.compare"
                    sx={{ flex: '1 1 220px', maxWidth: 300, m: 0 }}
                    control={<Checkbox size="small" checked={comparePrev} onChange={e => setComparePrev(e.target.checked)} inputProps={{ 'aria-label': 'Compare with previous year end' }} />}
                    label="Compare with previous year end"
                />
                <Button data-help="accounting.balanceSheet.apply" variant="contained" onClick={fetchReport} disabled={loading}>
                    {loading ? 'Loading...' : 'Apply'}
                </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <Button data-help="accounting.balanceSheet.exportCsv"
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                    disabled={loading || !hasData}
                >
                    Export CSV
                </Button>
                <Button data-help="accounting.balanceSheet.exportPdf" variant="outlined" startIcon={<PdfIcon />} onClick={() => handleDownload('pdf')} disabled={loading || downloading || !hasData}>
                    PDF
                </Button>
                <Button data-help="accounting.balanceSheet.exportXlsx" variant="outlined" startIcon={<XlsxIcon />} onClick={() => handleDownload('xlsx')} disabled={loading || downloading || !hasData}>
                    Excel
                </Button>
                <ExpandControls onExpandAll={() => setExpand(expandAll())} onCollapseAll={() => setExpand(collapseAll())} />
                {/* W3-C */}
                <Button data-help="accounting.balanceSheet.eliminated" component={RouterLink} to="/accounting/inter-segment/elimination" variant="outlined">
                    All segments (eliminated)
                </Button>
            </div>

            {/* Loading state */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
            )}

            {/* Empty state */}
            {!loading && !hasData && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    No data available for the selected date.
                </div>
            )}

            {/* Segment blocks */}
            {!loading && hasData && segmentBlocks().map((block: SegmentBalanceSheetBlock) => {
                const others = comparatives.map(c => blockOf(c.report, block.segmentId));
                const periodLines = (pick: (b: SegmentBalanceSheetBlock) => SegmentBalanceSheetBlock['assetLines']) =>
                    [pick(block), ...others.map(o => (o ? pick(o) : []))];
                const vals = (pick: (b: SegmentBalanceSheetBlock) => number | undefined) =>
                    [pick(block) ?? 0, ...others.map(o => (o ? pick(o) ?? 0 : 0))];
                const scopeBase = block.segmentId ?? 'unassigned';
                const ledgerHref = (accountId: string) => ledgerLink(accountId, ledgerFrom, applied!.asOf, applied!.segmentId);
                return (
                    <div key={block.segmentId ?? 'unassigned'} style={{ marginBottom: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                                {block.segmentCode ? `${block.segmentCode} — ` : ''}{block.segmentName}
                            </span>
                            <Chip
                                label={block.balanced ? 'Balanced' : 'Unbalanced'}
                                size="small"
                                color={block.balanced ? 'success' : 'error'}
                            />
                        </div>

                        {/*
                          * One scroll box and ONE table for the whole block: Assets, Liabilities and
                          * Equity share a single column grid under one header, so a total can never
                          * scroll out from under the heading it is read against.
                          */}
                        <ScrollableTable>
                            <table data-help-section="accounting.balanceSheet.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 + comparatives.length * 330 }}>
                                {renderHeader()}
                                <tbody>
                                    <SectionLabelRow label="Assets" colSpan={colSpan} />
                                    <StatementTreeRows scope={`${scopeBase}:asset`} perPeriod={periodLines(b => b.assetLines)}
                                        accounts={accounts} state={expand} onToggle={onToggle} ledgerHref={ledgerHref} />
                                    <TotalRow label="Total Assets" background="#e3f2fd" color="#1565c0" borderTop="2px solid #90caf9"
                                        values={vals(b => b.totalAssets)} />

                                    <SectionLabelRow label="Liabilities" colSpan={colSpan} />
                                    <StatementTreeRows scope={`${scopeBase}:liab`} perPeriod={periodLines(b => b.liabilityLines)}
                                        accounts={accounts} state={expand} onToggle={onToggle} ledgerHref={ledgerHref} />
                                    <TotalRow label="Total Liabilities" background="#fce4ec" borderTop="2px solid #f48fb1"
                                        values={vals(b => b.totalLiabilities)} />

                                    <SectionLabelRow label="Equity" colSpan={colSpan} />
                                    <StatementTreeRows scope={`${scopeBase}:equity`} perPeriod={periodLines(b => b.equityLines)}
                                        accounts={accounts} state={expand} onToggle={onToggle} ledgerHref={ledgerHref} />
                                    <TotalRow label="Retained Earnings (previous years)" background="#fff8e1" italic
                                        values={vals(b => b.retainedEarnings)} />
                                    <TotalRow
                                        label={`Current Year Earnings${report!.financialYearStart ? ` (from ${formatDate(report!.financialYearStart)})` : ''}`}
                                        background="#fff8e1" italic values={vals(b => b.currentPeriodEarnings)} />
                                    <TotalRow label="Total Equity" background="#ede7f6" borderTop="2px solid #b39ddb"
                                        values={vals(b => b.totalEquity)} />
                                    <TotalRow label="Total Liabilities & Equity" background="#f3e5f5" color="#6a1b9a" borderTop="2px solid #ce93d8"
                                        values={vals(b => b.totalLiabilitiesAndEquity)} />
                                </tbody>
                            </table>
                        </ScrollableTable>
                    </div>
                );
            })}

            {/* Grand-total footer */}
            {!loading && hasData && (
                <div style={{ marginTop: '8px' }}>
                    <ScrollableTable>
                        <table data-help-section="accounting.balanceSheet.table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 + comparatives.length * 330 }}>
                            {renderHeader()}
                            <tbody>
                                <TotalRow label="Total Assets" background="#e3f2fd" color="#1565c0" borderTop="3px solid #42a5f5"
                                    values={[report!.totalAssets, ...comparatives.map(c => c.report.totalAssets)]} />
                                <TotalRow label="Total Liabilities & Equity" background="#f3e5f5" color="#6a1b9a"
                                    values={[report!.totalLiabilitiesAndEquity, ...comparatives.map(c => c.report.totalLiabilitiesAndEquity)]} />
                                <tr style={{ backgroundColor: '#e8eaf6', fontWeight: 700, borderTop: '2px solid #7986cb' }}>
                                    <td style={statementCellStyle} colSpan={colSpan}>
                                        <span style={{ marginRight: '12px' }}>Balance Check</span>
                                        <Chip
                                            label={report!.balanced ? 'Balanced' : 'Unbalanced'}
                                            size="small"
                                            color={report!.balanced ? 'success' : 'error'}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </ScrollableTable>
                </div>
            )}
        </div>
    );
};

export default SegmentBalanceSheet;
